import { onBeforeUnmount, onMounted, readonly, ref, type Ref } from 'vue'

/**
 * 板块进场与动效状态。
 * - revealed：滚动到板块内触发一次，用于进场动画
 * - active：带 160px 前置余量，用于「离屏暂停」以及精灵图的按需加载
 */
export function useSectionReveal(section: Ref<HTMLElement | null>) {
  let observer: IntersectionObserver | null = null
  let motionObserver: IntersectionObserver | null = null
  const revealed = ref(false)
  const active = ref(false)

  onMounted(() => {
    const element = section.value
    if (!element) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion || !('IntersectionObserver' in window)) {
      element.classList.add('is-revealed')
      element.classList.add('is-motion-active')
      revealed.value = true
      active.value = true
      return
    }

    motionObserver = new IntersectionObserver(([entry]) => {
      element.classList.toggle('is-motion-active', entry.isIntersecting)
      // 一旦进入过预加载范围就保持为真：精灵图不必反复卸载重载
      if (entry.isIntersecting) active.value = true
    }, {
      threshold: 0,
      rootMargin: '160px 0px',
    })
    motionObserver.observe(element)

    element.classList.add('reveal-pending')
    observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      element.classList.add('is-revealed')
      revealed.value = true
      observer?.disconnect()
      observer = null
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -10% 0px',
    })
    observer.observe(element)
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    motionObserver?.disconnect()
  })

  return { revealed: readonly(revealed), active: readonly(active) }
}
